import { useState } from "react";
import { Section } from "../../components/AppShell";
import { Checkbox } from "../../components/fields";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Button } from "../../components/ui";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { useMe, useRotateCode, useSendQr, useSetPublic } from "../../api/queries";
import { ApiError, messagesOf } from "../../api/errors";
import { openExternal, shareToChat } from "../../telegram/webapp";
import { haptic } from "../../telegram/haptics";
import { useT } from "../../i18n/strings";
import type { Artwork } from "../../api/types";
import { QrCode } from "./QrCode";
import s from "./share.module.css";

/**
 * The owner's switch for the public page a printed tag opens. Renders nothing
 * until the server says where that page lives.
 */
export function PublicPageSection({ artwork }: { artwork: Artwork }) {
  const t = useT();
  const toast = useToast();
  const me = useMe();
  const setPublic = useSetPublic();
  const rotate = useRotateCode();
  const sendQr = useSendQr();
  const [confirming, setConfirming] = useState(false);

  const base = me.data?.public_page?.base_url;
  if (!base) return null;
  const link = `${base}/p/${artwork.public_code}`;
  const firstOf = (error: unknown) => messagesOf(error)[0] ?? t.errors.title;
  const fail = (error: unknown) => toast.show(firstOf(error), { tone: "error" });

  const toggle = (value: boolean) =>
    setPublic.mutate(
      { id: artwork.id, value },
      {
        onSuccess: () => toast.show(value ? t.share.published : t.share.unpublished),
        onError: fail,
      },
    );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      haptic.success();
      toast.show(t.share.copied);
    } catch {
      toast.show(t.share.copyFailed, { tone: "error" });
    }
  };

  const onSendQr = () =>
    sendQr.mutate(artwork.id, {
      onSuccess: () => {
        haptic.success();
        toast.show(t.share.sent);
      },
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        toast.show(
          status === 409 ? t.share.startBot : status === 502 ? t.share.sendFailed : firstOf(error),
          { tone: "error" },
        );
      },
    });

  const onRotate = () => {
    setConfirming(false);
    rotate.mutate(artwork.id, {
      onSuccess: () => {
        haptic.success();
        toast.show(t.share.rotated);
      },
      onError: fail,
    });
  };

  return (
    <Section title={t.share.title} icon="qr">
      <div className={s.body}>
        <Checkbox
          checked={artwork.is_public}
          onChange={(value) => !setPublic.isPending && toggle(value)}
        >
          {t.share.toggle}
        </Checkbox>
        <p className={s.intro}>{artwork.is_public ? t.share.intro : t.share.offNote}</p>

        {artwork.is_public && (
          <>
            <figure className={s.plate}>
              <QrCode value={link} label={t.share.qrLabel} />
              <figcaption className={s.caption}>{t.share.printHint}</figcaption>
            </figure>
            <p className={s.link}>{link}</p>
            <div className={s.actions}>
              <Button icon="copy" onClick={copy}>
                {t.share.copy}
              </Button>
              <Button icon="share" onClick={() => shareToChat(link, artwork.title)}>
                {t.share.send}
              </Button>
              <Button icon="openLink" onClick={() => openExternal(link)}>
                {t.share.open}
              </Button>
            </div>
            <Button
              icon="download"
              className={s.download}
              disabled={sendQr.isPending}
              onClick={onSendQr}
            >
              {sendQr.isPending ? t.share.downloading : t.share.download}
            </Button>
            <p className={s.intro}>{t.share.downloadHint}</p>
            <button
              type="button"
              className={s.rotate}
              disabled={rotate.isPending}
              onClick={() => setConfirming(true)}
            >
              <Icon name="refresh" size={16} />
              {t.share.rotate}
            </button>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title={t.share.rotateTitle}
        body={t.share.rotatePrompt}
        confirmLabel={t.share.rotate}
        onConfirm={onRotate}
        onCancel={() => setConfirming(false)}
      />
    </Section>
  );
}
